<template>
  <div>
    <v-toolbar
      flat
      color="white">
      <v-toolbar-title>{{ $t('heading') }}</v-toolbar-title>
      <v-spacer/>
      <v-select
        v-model="currentYear"
        :items="availableYears"
        :loading="isLoading"
        :disabled="isLoading"
        class="my-5"
        @change="loadHolidaysData"/>
    </v-toolbar>

    <template v-if="holidays && holidays.length">
      <v-list
        v-for="(item, index) in holidays"
        :key="index"
        two-line >
        <v-list-tile >
          <v-list-tile-content>
            <v-list-tile-title>
              {{ $t('holidayCreated') }}
              <span class="grey--text text--lighten-1">{{ createdFromNow(item.created_at) }}</span>
            </v-list-tile-title>
            <v-list-tile-sub-title>{{ format(item.from) }} - {{ format(item.to) }}</v-list-tile-sub-title>
          </v-list-tile-content>
          <v-btn
            icon
            @click="openDeleteDialog(item.id)">
            <v-icon color="error">delete</v-icon>
          </v-btn>
        </v-list-tile>

        <v-divider
          v-if="index + 1 < holidays .length"
          :key="'divider-' + item.id"/>
      </v-list>
    </template>

    <v-progress-linear
      v-else-if="!holidays"
      height="2"
      indeterminate/>

    <v-list
      v-else
      two-line
      subheader>
      <v-list-tile avatar>
        <v-list-tile-content>
          <v-list-tile-title> {{ $t('noInfoYet') }}</v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>
    </v-list>

    <employee-settings-holidays-entry-dialog
      v-model="createEntryDialog"
      @updateOnSuccess="loadHolidaysData"/>

    <confirm-dialog
      :is-delete="true"
      :is-open="inactivityDeleteDialog"
      :dialog-header="$t('deleteHeader')"
      :dialog-body="$t('deleteBody')"
      :is-button-disabled="!isDeleteHoliday"
      :confirm-button-text="$t('delete')"
      :is-loading="isDeleting"
      @confirm="deleteEntry"
      @dialogToggle="val => inactivityDeleteDialog = val"
    >
      <v-checkbox
        v-model="isDeleteHoliday"
        :label="$t('holidayDeleteWarning')"
        :value="false"
        :checked="true"
        color="success"
        class="mx-3"/>
    </confirm-dialog>

    <v-card-actions>
      <v-spacer/>

      <disabled-user-button>
        <v-btn
          slot-scope="{isDisabledForUser}"
          :disabled="isDisabledForUser"
          color="primary"
          @click.native="createEntryDialog = true"
        >
          {{ $t('createEntry') }}
        </v-btn>
      </disabled-user-button>

    </v-card-actions>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import HolidaysApi from '@/api/HolidaysApi'
import DisabledUserButton from '@/components/global-components/disabledUserButton/disabledUserButton'
import Pagination from '@/components/global-components/pagination/Pagination'
import EmployeeSettingsHolidaysEntryDialog
  from '@/components/routes/employees/settings/EmployeeSettingsHolidaysEntryDialog'
import ConfirmDialog from '@/components/global-components/confirmDialog/ConfirmDialog'

export default {
  name: 'EmployeeSettingsHolidays',
  components: { ConfirmDialog, EmployeeSettingsHolidaysEntryDialog, Pagination, DisabledUserButton },
  data () {
    return {
      isDeleteHoliday: false,
      isDeleting: false,
      createEntryDialog: false,
      inactivityDeleteDialog: false,
      isLoading: false,
      currentDeleteId: null,
      holidays: null,
      currentYear: (new Date()).getFullYear(),
      availableYears: []
    }
  },
  computed: {
    ...mapGetters('employee', ['currentEmployee'])
  },
  methods: {
    openDeleteDialog (id) {
      this.currentDeleteId = id
      this.inactivityDeleteDialog = true
    },
    async deleteEntry () {
      this.isDeleting = true
      try {
        await HolidaysApi.deleteHolidays(this.currentDeleteId)
        this.loadHolidaysData()
      } catch (e) {
        this.$notification(e)
      }
      this.isDeleting = false
      this.currentDeleteId = null
      this.inactivityDeleteDialog = false
    },
    async getAvailableYears () {
      this.isLoading = true
      try {
        const result = await HolidaysApi.getAvailableYears(this.currentEmployee.schema.id)

        const isCurrentYearExist = result.includes(this.currentYear)
        if (!isCurrentYearExist) {
          result.push(this.currentYear)
        }

        this.availableYears = result
      } catch (e) {
        this.$notification(e)
      }
      this.isLoading = false
    },
    async getHolidays () {
      try {
        this.holidays = await HolidaysApi.getHolidaysByYear(this.currentEmployee.schema.id, this.currentYear)
      } catch (e) {
        this.$notification(e)
      }
    },
    async loadHolidaysData () {
      this.holidays = null
      await this.getAvailableYears()
      await this.getHolidays()
    },
    format (time) {
      return this.$moment(time, 'YYYY-MM-DD').format('DD MMMM YYYY')
    },
    createdFromNow (createdAt) {
      return this.$moment(createdAt, 'YYYY-MM-DD HH:mm:ss').fromNow()
    }
  },
  async created () {
    await this.loadHolidaysData()
  },
  i18n: {
    messages: {
      de: {
        heading: 'Mitarbeiterurlaub',
        holidayCreated: 'Urlaubzeitraum',
        noInfoYet: 'Keine Informationen',
        selectYear: 'Jahr',
        createEntry: 'Neuer Eintrag für Urlaub',
        deleteHeader: 'Bestätigung',
        deleteBody: 'Möchten Sie diesen Eintrag wirklich löschen?',
        holidayDeleteWarning: 'Das entfernen von Urlaub kann Stundenzettel invalide machen. Bitte bestätigen Sie, dass Sie diese auf Korrektheit prüfen. ',
        delete: 'Entfernen'
      },
      en: {
        heading: 'Employee vacation',
        holidayCreated: 'Holiday created',
        noInfoYet: 'No Data',
        selectYear: 'Year',
        createEntry: 'New vacation entry',
        deleteHeader: 'Confirm',
        deleteBody: 'Do you really want to delete this entry?',
        holidayDeleteWarning: 'Removing vacation entries can make timesheets invalid. Please confirm that you are checking them for correctness. ',
        delete: 'Delete'
      },
      tr: {
        heading: 'Çalışan tatili',
        holidayCreated: 'Tatil yaratıldı',
        noInfoYet: 'Veri yok',
        selectYear: 'Yıl',
        createEntry: 'Yeni tatil girişi',
        deleteHeader: 'Onayla',
        deleteBody: 'Bu kaydı silmek istediğinize emin misiniz?',
        holidayDeleteWarning: 'Tatil girişlerini silmek zaman çizelgelerini geçersiz yapabilir. Lütfen doğruluğunu kontrol ettiğinizi onaylayınız. ',
        delete: 'Sil'
      },
      ru: {
        heading: 'Отпуск сотрудника',
        holidayCreated: 'Отпуск создан',
        noInfoYet: 'Нет данных',
        selectYear: 'Год',
        createEntry: 'Создать отпуск',
        deleteHeader: 'Подтвердить',
        deleteBody: 'Вы действительно хотите удалить эту запись?',
        holidayDeleteWarning: 'Удаление отпусков может сделать табели недействительными. Пожалуйста, подтвердите, что вы проверили их на корректность.',
        delete: 'Удалить'
      }
    }
  }
}
</script>
